/* Modèle du Cockpit CFO — mappe les moteurs financiers existants
   (buildSynthesisModel + buildForecastModel) vers le tableau d'indicateurs
   affiché par l'écran. Aucun chiffre fabriqué : donnée absente → null.

   Narratif DIFFÉRÉ (décision 2026-07-04) : pas de verdict / « Pourquoi ? »
   inventé. Chaque indicateur porte ses chiffres réels, ses séries mensuelles et
   ses drivers = décomposition NUMÉRIQUE réelle du KPI. Le statut couleur est
   dérivé par règle objective sur les chiffres (pas un jugement rédigé). */

import { useEffect, useMemo, useState } from 'react';
import { getBsView } from '../../api/financials_bs';
import { getPlView } from '../../api/financials_pl';
import { getCapacityOverview } from '../../api/capacityOverview';
import { getCashActuals } from '../../api/financialsCashActuals';
import { listIndirectCosts } from '../../api/indirectCosts';
import { listHrContracts } from '../../api/hr';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { buildSynthesisModel } from '../financials/synthesisModel';
import { useForecastData, buildForecastModel } from '../forecastModel';
import { p, tp } from '../../i18n/useP';

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const sumN = (arr, n) => (Array.isArray(arr) ? arr.slice(0, n).reduce((a, v) => a + num(v), 0) : null);
const sum12 = (arr) => (Array.isArray(arr) ? arr.reduce((a, v) => a + num(v), 0) : null);

/* Clés de page cible des CTA « Voir le détail complet » (sections du menu
   Finance). Depuis l'ACCÈS DIRECT aux états (2026-09-17), chaque cible est une
   SECTION : le relais `reports_pending_tab`, qui demandait un onglet au hub
   Rapports, n'a plus d'objet. Le plan de liquidité visait la Simulation 12M
   (`treasury`), partie à l'Atelier donc invisible chez un client ; il vise
   maintenant le plan lui-même. */
export const TAB_TARGET = {
  synthesis: 'financials-synthesis',
  pl: 'financials-pl',
  cash: 'financials-cash-plan',
  bfre: 'financials-indicators',
};

/* Charge toutes les données réelles + prévisionnelles, comme FinancialsSynthesisTab. */
export function useCockpitData(tenantId, year) {
  const [bs, setBs] = useState(null);
  const [bsPrev, setBsPrev] = useState(null);
  const [pl, setPl] = useState(null);
  const [plPrev, setPlPrev] = useState(null);
  const [plPrev2, setPlPrev2] = useState(null);
  const [cap, setCap] = useState(null);
  const [indirect, setIndirect] = useState(null);
  const [contracts, setContracts] = useState(null);
  const [creditLimit, setCreditLimit] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId || !year) {
      setBs(null); setBsPrev(null); setPl(null); setPlPrev(null); setPlPrev2(null);
      setCap(null); setIndirect(null); setContracts(null); setLoading(false); return;
    }
    let alive = true;
    setLoading(true);
    Promise.all([
      getBsView(tenantId, year),
      getBsView(tenantId, year - 1).catch(() => null),
      getPlView(tenantId, year),
      getPlView(tenantId, year - 1).catch(() => null),
      getPlView(tenantId, year - 2).catch(() => null),
      getCapacityOverview(tenantId, year).catch(() => null),
      getCashActuals(tenantId, year).catch(() => null),
      listIndirectCosts(tenantId, year).catch(() => null),
      listHrContracts(tenantId).catch(() => null),
    ]).then(([bsR, bsPrevR, plR, plPrevR, plPrev2R, capR, caR, indR, ctrR]) => {
      if (!alive) return;
      setBs(bsR?.data || null);
      setBsPrev(bsPrevR?.data || null);
      setPl(plR?.data || null);
      setPlPrev(plPrevR?.data || null);
      setPlPrev2(plPrev2R?.data || null);
      setCap(capR?.data || null);
      setIndirect(indR?.data || null);
      setContracts(Array.isArray(ctrR?.data) ? ctrR.data : (Array.isArray(ctrR) ? ctrR : null));
      const cc = Number.isFinite(plR?.data?.closed_count) ? plR.data.closed_count : 12;
      const months = caR?.data?.months || [];
      const lastReal = months[Math.max(0, Math.min(11, cc - 1))];
      setCreditLimit(Number(lastReal?.credit_line) || 0);
      setLoading(false);
    }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tenantId, year]);

  const m = useMemo(
    () => buildSynthesisModel({ bs, bsPrev, pl, plPrev, plPrev2, cap, indirect, contracts, creditLimit, year }),
    [bs, bsPrev, pl, plPrev, plPrev2, cap, indirect, contracts, creditLimit, year],
  );

  // Prévisionnel : mêmes hypothèses BFRE que le hub Prévisionnel (mêmes clés).
  const aKey = `prev_wc_${tenantId}_${year}`;
  const [dso] = usePersistentValue(`${aKey}_dso`, '');
  const [dpo] = usePersistentValue(`${aKey}_dpo`, '');
  const [dio] = usePersistentValue(`${aKey}_dio`, '');
  const [pac] = usePersistentValue(`${aKey}_pac`, '');
  const fdata = useForecastData(tenantId, year);
  const fm = useMemo(
    () => buildForecastModel(fdata.plView, fdata.bsCur, fdata.bsPrev, fdata.plPrev,
      { dso, dpo, dio, pac }, { bsPrev2: fdata.bsPrev2, fteCur: fdata.fteCur, ftePrev: fdata.ftePrev }),
    [fdata.plView, fdata.bsCur, fdata.bsPrev, fdata.bsPrev2, fdata.plPrev, fdata.fteCur, fdata.ftePrev, dso, dpo, dio, pac],
  );

  return { m, fm, creditLimit, loading: loading || fdata.loading };
}

/* Statut objectif (couleur) dérivé par règle sur les chiffres — jamais un texte.
   ref fournie → position vs référence ; sinon → variation relative vs N-1.
   higherIsBetter=false inverse (ex. runway court = mauvais géré à part). */
function statusByDelta(deltaRatio) {
  if (deltaRatio == null) return 'attention';
  if (deltaRatio >= -0.02) return 'favorable';
  if (deltaRatio >= -0.15) return 'attention';
  return 'critique';
}
function statusByRef(value, ref) {
  if (value == null || ref == null) return 'attention';
  if (value >= ref) return 'favorable';
  if (value >= ref * 0.9) return 'attention';
  return 'critique';
}

/* Texte de variation (delta) — factuel, calculé. Ex. « −67% vs 2025 ». */
function deltaText(cur, ref, { pts = false } = {}) {
  if (cur == null || ref == null || ref === 0) return '—';
  if (pts) {
    const d = cur - ref;
    return `${d >= 0 ? '+' : '−'}${Math.abs(d).toFixed(2)} ${p('pt vs 2025')}`;
  }
  const d = cur / ref - 1;
  return `${d >= 0 ? '+' : '−'}${Math.abs(d * 100).toFixed(0)}% ${p('vs 2025')}`;
}

/* Construit le tableau d'indicateurs à partir des deux modèles. */
export function buildCockpitKpis({ m, fm, creditLimit }) {
  if (!m || !fm) return null;
  const cc = Number.isFinite(fm.closedCount) ? fm.closedCount : (m.closed || 12);
  const P = fm.pnl || {};
  const N1 = fm.pnlN1 || {};
  const cash = fm.cash || {};

  // ── Séries mensuelles (réel jusqu'à cc, prévision ensuite) ────────────────
  const revM = P.revenue || null;
  const vaM = P.valueAdded || null;
  const mbM = P.grossMargin || null;
  const ebM = P.ebitda || null;
  const persM = P.personnel || null;
  // CAF « avant coût de financement » : on retraite le résultat financier (intérêts
  // dominants) pour isoler ce que l'exploitation génère indépendamment de la dette
  // (base des ratios de couverture). `financial` est négatif → on le SOUSTRAIT pour
  // le neutraliser. Le résultat net global reste inchangé (équilibre bilan).
  const cafM = (cash.netResult && cash.amort)
    ? cash.netResult.map((v, i) => num(v) + num(cash.amort[i]) - num(cash.financial?.[i])) : null;
  // Repères N-1 mensuels — seul le CA est disponible mois par mois (m.revBars.prev).
  const revM25 = Array.isArray(m.revBars) ? m.revBars.map((b) => (b.prev == null ? null : num(b.prev))) : null;

  // ── Rentabilité ───────────────────────────────────────────────────────────
  const caYtd = sumN(revM, cc);
  const vaYtd = sumN(vaM, cc);
  const mbYtd = sumN(mbM, cc);
  const ebYtd = sumN(ebM, cc);
  const persYtd = sumN(persM, cc);              // négatif (charge)
  const caFy = sum12(revM);
  const vaFy = sum12(vaM);
  const mbFy = sum12(mbM);
  const ebFy = sum12(ebM);

  const rate = (v) => (caYtd ? (num(v) / caYtd) * 100 : null);   // % du CA net YTD

  // CA net / EPT (annualisé) — depuis synthesisModel (capacité).
  const caEpt = m.caEpt;
  const caEptN1 = m.caEptDelta != null && caEpt != null ? caEpt / (1 + m.caEptDelta / 100) : null;

  const eptAvg = fm.kpi?.eptAvg ?? m.eptReal ?? null;
  const eptAvgN1 = fm.kpi?.eptAvgN1 ?? null;
  const coutEpt = (eptAvg && persYtd) ? (Math.abs(persYtd) * 12 / cc) / eptAvg : null;
  const coutEptN1 = (eptAvgN1 && N1.personnel) ? Math.abs(N1.personnel) / eptAvgN1 : null;
  // Rendement masse salariale = VA / |charges personnel| (plus cohérent que CA :
  // hors mix marchandises). VA/EPT annualisée pour les barres du drawer.
  const vaEptAnn = (eptAvg && vaYtd) ? (vaYtd * 12 / cc) / eptAvg : null;
  const vaEptAnnN1 = (eptAvgN1 && N1.valueAdded) ? N1.valueAdded / eptAvgN1 : null;
  const rendementVA = persYtd ? vaYtd / Math.abs(persYtd) : null;
  const rendementVAN1 = (N1.valueAdded && N1.personnel) ? N1.valueAdded / Math.abs(N1.personnel) : null;
  // CA net 12 mois glissant (courbe) + EPT mensuel (barres) pour le drawer CA/EPT.
  const caRoll = Array.isArray(m.rolling) ? m.rolling.map((p) => (Number.isFinite(p?.rev) ? p.rev : null)) : null;
  const eptMonthsArr = fm.kpi?.eptMonths || null;
  // Productivité : VA 12 mois glissants / effectif moyen (kCHF par EPT). Insensible
  // au mix marchandises (contrairement au ratio VA/CA distordu par les reventes).
  const vaRoll = Array.isArray(m.rolling) ? m.rolling.map((p) => (Number.isFinite(p?.va) ? p.va : null)) : null;
  const vaEptRoll = (vaRoll && eptAvg) ? vaRoll.map((v) => (Number.isFinite(v) ? v / eptAvg : null)) : null;
  const vaEptLast = Array.isArray(vaEptRoll) ? [...vaEptRoll].reverse().find((v) => Number.isFinite(v)) ?? null : null;
  // Effet ciseaux : croissance des revenus vs des charges opérationnelles sur la
  // fenêtre 12 mois glissants (premier → dernier point). Écart en points = ciseau.
  const rollCis = (m.rolling || []).filter((p) => Number.isFinite(p?.rev) && Number.isFinite(p?.op));
  let cisRevG = null; let cisChgG = null; let cisSpread = null; let cisLast = null;
  let cisRevEffect = null; let cisChgEffect = null; let cisEbitImpact = null;
  if (rollCis.length >= 2) {
    const f = rollCis[0]; cisLast = rollCis[rollCis.length - 1];
    cisRevG = f.rev ? (cisLast.rev - f.rev) / f.rev : null;
    cisChgG = f.op ? (cisLast.op - f.op) / f.op : null;
    if (cisRevG != null && cisChgG != null) cisSpread = cisRevG - cisChgG;
    // Chiffrage EBIT : EBITDA glissant = Revenus − Charges → ΔEBITDA = Δrev − Δcharges.
    cisRevEffect = cisLast.rev - f.rev;          // effet revenus (négatif si CA baisse)
    cisChgEffect = -(cisLast.op - f.op);         // effet charges (négatif si charges montent)
    cisEbitImpact = cisRevEffect + cisChgEffect; // impact EBIT total sur 12 mois
  }
  const pctG = (v) => (v == null ? '—' : `${v >= 0 ? '+' : '−'}${Math.abs(v * 100).toFixed(0)} %`);
  const spreadTxt = (v) => (v == null ? '–' : `${v >= 0 ? '+' : '−'}${Math.abs(v * 100).toFixed(0)} pts`);

  // ── Liquidité & solidité ──────────────────────────────────────────────────
  const treso = m.liq;                                   // solde de clôture (dernier mois clos)
  const tresoProj = Array.isArray(cash.runLiq)
    ? [...cash.runLiq].reverse().find((v) => Number.isFinite(v)) ?? null : null;
  const tresoN1 = Array.isArray(m.liqMonthlyPrev)
    ? [...m.liqMonthlyPrev].reverse().find((v) => Number.isFinite(v)) ?? null : null;
  // Projection trésorerie raccordée au dernier réel (masquée sur les mois clos).
  const projTreso = Array.isArray(cash.runLiq)
    ? cash.runLiq.map((v, i) => (i >= cc - 1 && Number.isFinite(v) ? v : null)) : null;
  const reelTreso = Array.isArray(m.liqMonthly)
    ? m.liqMonthly.filter((v) => Number.isFinite(v)) : null;

  const cafYtd = sumN(cafM, cc);
  const cafFy = sum12(cafM);
  const cafN1 = (cash.netResultN1 != null && cash.amortN1 != null)
    ? num(cash.netResultN1) + num(cash.amortN1) - num(cash.financialN1) : null;

  // Composition FRE mensuelle (lignes BFRE réelles du forecastModel + trésorerie).
  const stackComps = buildFreStack(fm);

  const kpis = [
    // ── Groupe Rentabilité ──────────────────────────────────────────────────
    {
      key: 'ca', group: 'rent', type: 'chf', mode: 'flow', showEcart: true,
      label: tp('phrases:p_d1bc74fc51'), unit: 'kCHF',
      ytd: caYtd, proj: caFy, n1: N1.revenue ?? null,
      status: statusByDelta(N1.revenue ? caFy / N1.revenue - 1 : null),
      delta: deltaText(caFy, N1.revenue),
      trend: tailSpark(revM, cc),
      m2025: revM25, m2026: revM, actualN: cc,
      // Mix des revenus (C6) : val + % du mix, actuel (cumulé à ce jour) et 2025
      // (année pleine), sans delta.
      mix: [
        [tp('phrases:p_feb205daaf'), P.revPrestations, N1.revPrestations],
        [tp('phrases:p_5ea3dfcedf'), P.revMarchandises, N1.revMarchandises],
        [tp('phrases:p_6450904635'), P.revAutres, N1.revAutres],
      ].map(([label, arr, v25]) => {
        const val = sumN(arr, cc);
        return { label, val, pct: (caYtd ? (val / caYtd) * 100 : null), val25: v25 ?? null, pct25: (N1.revenue ? ((v25 || 0) / N1.revenue) * 100 : null) };
      }),
      drivers: [
        [tp('phrases:p_feb205daaf'), sumN(P.revPrestations, cc)],
        [tp('phrases:p_5ea3dfcedf'), sumN(P.revMarchandises, cc)],
        [tp('phrases:p_6450904635'), sumN(P.revAutres, cc)],
      ],
      tab: tp('phrases:p_a292527bbe'), target: TAB_TARGET.pl,
      statLabels: [tp('phrases:p_ef062eb7ae'), tp('phrases:p_2ecec92601'), tp('phrases:p_b34f817280')],
    },
    {
      key: 'va', group: 'rent', type: 'chf', mode: 'vaept', showEcart: true, label: tp('phrases:p_a372154f84'), unit: 'kCHF',
      ytd: vaYtd, proj: vaFy, n1: N1.valueAdded ?? null,
      status: statusByDelta(N1.valueAdded ? vaFy / N1.valueAdded - 1 : null),
      delta: deltaText(vaFy, N1.valueAdded),
      rateVal: rate(vaYtd), trend: tailSpark(vaM, cc),
      vaEptRoll, vaEptLast, vaEptAnn, vaEptAnnN1, eptAvg,
      drivers: [[tp('phrases:p_d1bc74fc51'), caYtd], [tp('phrases:p_54448eb93d'), sumN(P.cogs, cc)]],
      tab: tp('phrases:p_a292527bbe'), target: TAB_TARGET.pl,
      statLabels: [tp('phrases:p_ef062eb7ae'), tp('phrases:p_2ecec92601'), tp('phrases:p_b34f817280')],
    },
    {
      key: 'mb', group: 'rent', type: 'chf', mode: 'rate', showEcart: true, label: tp('phrases:p_72223e53da'), unit: 'kCHF',
      ytd: mbYtd, proj: mbFy, n1: N1.grossMargin ?? null,
      status: statusByDelta(N1.grossMargin ? mbFy / N1.grossMargin - 1 : null),
      delta: deltaText(mbFy, N1.grossMargin),
      rateVal: rate(mbYtd), trend: tailSpark(mbM, cc),
      driverBase: caYtd,
      drivers: [[tp('phrases:p_d1bc74fc51'), caYtd], [tp('phrases:p_54448eb93d'), sumN(P.cogs, cc)], [tp('phrases:p_8f7d854255'), persYtd]],
      tab: tp('phrases:p_a292527bbe'), target: TAB_TARGET.pl,
      statLabels: [tp('phrases:p_ef062eb7ae'), tp('phrases:p_2ecec92601'), tp('phrases:p_b34f817280')],
    },
    {
      key: 'eb', group: 'rent', type: 'chf', mode: 'ebitda', showEcart: true, label: 'EBITDA', unit: 'kCHF',
      ytd: ebYtd, proj: ebFy, n1: N1.ebitda ?? null,
      status: statusByDelta(N1.ebitda ? ebFy / N1.ebitda - 1 : null),
      delta: deltaText(ebFy, N1.ebitda),
      months: ebM, actualN: cc,
      rateVal: rate(ebYtd), trend: tailSpark(ebM, cc),
      cascade: buildCascadeSteps(caYtd, sumN(P.cogs, cc), persYtd, sumN(P.indirect, cc), vaYtd, mbYtd, ebYtd),
      drivers: [[tp('phrases:p_d1bc74fc51'), caYtd], [tp('phrases:p_a92c676527'), sumN(P.cogs, cc)], [tp('phrases:p_6e207a8a45'), persYtd], [tp('phrases:p_d33c34720f'), sumN(P.indirect, cc)]],
      tab: tp('phrases:p_a292527bbe'), target: TAB_TARGET.pl,
      statLabels: [tp('phrases:p_ef062eb7ae'), tp('phrases:p_2ecec92601'), tp('phrases:p_b34f817280')],
    },
    {
      key: 'caept', group: 'rent', type: 'chf', mode: 'caept', noProj: true, defaultHidden: true,
      label: tp('phrases:p_57127c32f0'), unit: 'kCHF',
      ytd: caEpt, proj: null, n1: caEptN1,
      status: statusByDelta(m.caEptDelta != null ? m.caEptDelta / 100 : null),
      delta: m.caEptDelta != null ? `${m.caEptDelta >= 0 ? '+' : '−'}${Math.abs(m.caEptDelta).toFixed(0)}% ${p('vs 2025')}` : '—',
      subOverride: eptAvg ? `${p('annualisé')} · ${p('effectif moyen')} ${eptAvg.toFixed(1)} EPT` : p('annualisé'),
      caRoll, eptMonths: eptMonthsArr, actualN: cc, trend: null,
      drivers: [[tp('phrases:p_0b24a01002'), m.caEpt != null && eptAvg ? m.caEpt * eptAvg : null], [tp('phrases:p_bae00c8ee9'), eptAvg]],
      statsOverride: [[tp('phrases:p_b34bba20b1'), fmtChf(caEpt)], [tp('phrases:p_f65754a0b8'), eptAvg != null ? `${eptAvg.toFixed(1)} EPT` : '—'], ['2025', fmtChf(caEptN1)]],
      tab: tp('phrases:p_ab761b3579'), target: TAB_TARGET.synthesis,
    },
    {
      key: 'ratioept', group: 'rent', type: 'ratio', mode: 'rendbars', noProj: true,
      label: tp('phrases:p_f349ae08f0'), sublabel: `(VA ÷ ${tp('phrases:p_6e207a8a45').toLowerCase()})`, unit: '',
      ytd: rendementVA, proj: null, n1: rendementVAN1,
      threshold: { value: 1.3, label: tp('phrases:p_0d4be11b74') },
      status: statusByRef(rendementVA, 1.3),
      delta: deltaText(rendementVA, rendementVAN1, { pts: true }),
      subOverride: tp('phrases:p_cb27b6644a'), trend: null,
      rendCmp: { caEpt: vaEptAnn, caEptN1: vaEptAnnN1, coutEpt, coutEptN1, rend: rendementVA, rendN1: rendementVAN1, label1: tp('phrases:p_2d40ff69ec') },
      drivers: [[tp('phrases:p_755b12eddd'), vaEptAnn], [tp('phrases:p_a52b96aae8'), coutEpt], [tp('phrases:p_bae00c8ee9'), eptAvg]],
      statsOverride: [[tp('phrases:p_2d40ff69ec'), fmtChf(vaEptAnn)], [tp('phrases:p_1943c277cd'), fmtChf(coutEpt)], [tp('phrases:p_30d52cf8d1'), rendementVA != null ? `${rendementVA.toFixed(2)}×` : '—']],
      tab: tp('phrases:p_ab761b3579'), target: TAB_TARGET.synthesis,
    },
    {
      key: 'ciseaux', group: 'rent', type: 'pts', mode: 'scissors', noProj: true,
      label: tp('phrases:p_9c18ffd89b'), sublabel: p('(revenus vs charges · 12 mois glissants)'), unit: '',
      ytd: cisSpread, proj: null, n1: null,
      valueOverride: spreadTxt(cisSpread),
      status: cisSpread == null ? 'attention' : (cisSpread >= 0.02 ? 'favorable' : cisSpread <= -0.02 ? 'critique' : 'attention'),
      delta: (cisRevG != null && cisChgG != null) ? `${tp('phrases:p_a08a099a37')} ${pctG(cisRevG)} · ${tp('phrases:p_2847e9bf8c')} ${pctG(cisChgG)}` : '—',
      subText: cisEbitImpact != null ? `≈ ${fmtChf(cisEbitImpact)} ${tp('phrases:p_5c2659fc54')}` : tp('phrases:p_d838448c51'),
      trend: null,
      rolling: m.rolling, revG: cisRevG, chgG: cisChgG, ebitImpact: cisEbitImpact,
      drivers: cisEbitImpact != null ? [
        [tp('phrases:p_650abd3b8e'), cisRevEffect],
        [tp('phrases:p_18c50b17e6'), cisChgEffect],
        [tp('phrases:p_0049ea7510'), cisEbitImpact],
      ] : [],
      statsOverride: [
        [tp('phrases:p_f0123a36f6'), pctG(cisRevG)],
        [tp('phrases:p_1c2722f3b6'), pctG(cisChgG)],
        [tp('phrases:p_9629ef5bf5'), cisEbitImpact != null ? fmtChf(cisEbitImpact) : '—'],
      ],
      tab: tp('phrases:p_a292527bbe'), target: TAB_TARGET.pl,
    },
    // ── Groupe Liquidité & solidité ─────────────────────────────────────────
    {
      key: 'tr', group: 'cash', type: 'chf', mode: 'liqproj', label: tp('phrases:p_e0e75da7e7'), unit: 'kCHF',
      ytd: treso, proj: tresoProj, n1: tresoN1,
      status: statusByDelta(tresoN1 ? treso / tresoN1 - 1 : null),
      delta: deltaText(treso, tresoN1),
      subText: tp('phrases:p_da385aea0d'), trend: tailSpark(m.liqMonthly, cc),
      lp: {
        reel: reelTreso,
        prev: projTreso ? projTreso.filter((v) => Number.isFinite(v)) : null,
        prevStart: Math.max(0, cc - 1),
        n1: Array.isArray(m.liqMonthlyPrev) ? m.liqMonthlyPrev : null,
        credit: creditLimit ? -Math.abs(creditLimit) : null,
      },
      drivers: [[tp('phrases:p_0b59c4dd16'), treso], [tp('phrases:p_85af6e70db'), tresoProj != null && treso != null ? tresoProj - treso : null]],
      tab: tp('phrases:p_b60dc8b60a'), target: TAB_TARGET.cash,
      statLabels: [tp('phrases:p_a702ea40e9'), tp('phrases:p_30827af276'), tp('phrases:p_653504a156')],
    },
    {
      key: 'run', group: 'cash', type: 'mois', mode: 'runway', label: tp('phrases:p_80a63a7686'), unit: p('mois'),
      ytd: m.runway, proj: null, n1: null,
      status: m.runway == null ? 'attention' : (m.runway >= 6 ? 'favorable' : m.runway >= 3 ? 'attention' : 'critique'),
      delta: m.burn != null ? `${p('burn')} ${fmtChf(-Math.abs(m.burn))} ${p('kCHF/mois')}` : '—',
      subText: tp('phrases:p_1c57ebaa9c'), trend: null,
      rw: buildRunway(m, cash, cc),
      drivers: [[tp('phrases:p_0e0af55ddf'), m.liq], [tp('phrases:p_219bfebd93'), m.burn != null ? -Math.abs(m.burn) : null]],
      statsOverride: [[tp('phrases:p_9282e742dd'), fmtChf(m.liq)], [tp('phrases:p_3f3cfe92ae'), m.burn != null ? fmtChf(-Math.abs(m.burn)) : '—'], [tp('phrases:p_80a63a7686'), m.runway != null ? `${m.runway.toFixed(1)} ${p('mois')}` : '—']],
      tab: tp('phrases:p_b60dc8b60a'), target: TAB_TARGET.cash,
    },
    {
      key: 'burn', group: 'cash', type: 'chf', mode: 'burnbars', noProj: true, label: tp('phrases:p_6f0294e5ec'), unit: p('kCHF/mois'),
      ytd: m.burn != null ? Math.abs(m.burn) : null, proj: null, n1: null,
      status: 'attention',
      delta: m.runway != null ? `${m.runway.toFixed(1)} ${p('mois de runway')}` : '—',
      subText: tp('phrases:p_0e566492c3'), trend: null,
      burnBars: m.burnBars || null,
      drivers: (m.burnComps || []).map((c) => [c[0], c[1]]),
      statsOverride: [
        [tp('phrases:p_3f3cfe92ae'), m.burn != null ? fmtChf(Math.abs(m.burn)) : '—'],
        [tp('phrases:p_07c868f4ee'), m.burnN1 != null ? fmtChf(m.burnN1) : '—'],
        [tp('phrases:p_80a63a7686'), m.runway != null ? `${m.runway.toFixed(1)} ${p('mois')}` : '—'],
      ],
      tab: tp('phrases:p_b60dc8b60a'), target: TAB_TARGET.cash,
    },
    {
      key: 'liq', group: 'cash', type: 'ratio', mode: 'pills', noProj: true,
      chartTitle: tp('phrases:p_9acedb4da0'),
      label: tp('phrases:p_aa7b84ffba'), sublabel: tp('phrases:p_f235252ace'), unit: '',
      ytd: m.r2, proj: null, n1: null,
      status: statusByRef(m.r2, 1.0),
      delta: m.r2 != null ? `${p('réduite')} ${m.r2.toFixed(2)}× · ${p('réf 1.0×')}` : '—',
      subText: tp('phrases:p_bde3b979da'), trend: null,
      pills: [
        { label: tp('phrases:p_4bce6c0d33'), deg: tp('phrases:p_c2751ab5a2'), val: m.r1, ref: 0.2, max: 1.6 },
        { label: tp('phrases:p_d377bbf285'), deg: tp('phrases:p_814f280a5f'), val: m.r2, ref: 1.0, max: 1.6 },
        { label: tp('phrases:p_787e6d0b34'), deg: tp('phrases:p_37bf111a25'), val: m.r3, ref: 1.5, max: 2.2 },
      ],
      drivers: [[tp('phrases:p_ad7d8bcd38'), m.liq], [tp('phrases:p_036e9d357b'), m.rec], [tp('phrases:p_00fe58633a'), m.curAssets], [tp('phrases:p_323c4b8082'), m.curLiab]],
      statsOverride: [[tp('phrases:p_4bce6c0d33'), m.r1 != null ? `${m.r1.toFixed(2)}×` : '—'], [tp('phrases:p_d377bbf285'), m.r2 != null ? `${m.r2.toFixed(2)}×` : '—'], [tp('phrases:p_787e6d0b34'), m.r3 != null ? `${m.r3.toFixed(2)}×` : '—']],
      tab: tp('phrases:p_d7d49e2131'), target: TAB_TARGET.pl,
    },
    {
      key: 'caf', group: 'cash', type: 'chf', mode: 'flow', label: tp('phrases:p_4544d7142a'), unit: 'kCHF',
      sublabel: tp('phrases:p_210c36976c'),
      ytd: cafYtd, proj: cafFy, n1: cafN1,
      status: statusByDelta(cafN1 ? cafFy / cafN1 - 1 : null),
      delta: deltaText(cafFy, cafN1),
      subText: tp('phrases:p_c41d7ef7c9'), trend: tailSpark(cafM, cc),
      m2025: null, m2026: cafM, actualN: cc,
      drivers: [
        [tp('phrases:p_667be72252'), sumN(cash.netResult, cc)],
        [tp('phrases:p_09f4847875'), sumN(cash.amort, cc)],
        [tp('phrases:p_f16a7facc1'), -sumN(cash.financial, cc)],
      ],
      tab: tp('phrases:p_b60dc8b60a'), target: TAB_TARGET.cash,
      statLabels: [tp('phrases:p_ef062eb7ae'), tp('phrases:p_2ecec92601'), tp('phrases:p_b34f817280')],
    },
    {
      key: 'fre', group: 'cash', type: 'chf', mode: 'stack', label: tp('phrases:p_65bc846076'), unit: 'kCHF',
      ytd: m.bfre, proj: null, n1: null,
      status: 'attention',
      delta: m.dso != null ? `DSO ${Math.round(m.dso)} ${p('j')}` : '—',
      subText: tp('phrases:p_4cd1b51727'), trend: null,
      stack: m.bfreStackRolling?.stack || stackComps,
      stackLabels: m.bfreStackRolling?.labels || null,
      drivers: [[tp('phrases:p_036e9d357b'), m.rec], [tp('phrases:p_a037c2c3c8'), m.stock], [tp('phrases:p_573a205416'), m.ap != null ? -Math.abs(m.ap) : null], [tp('phrases:p_07a1717518'), m.adv != null ? -Math.abs(m.adv) : null]],
      tab: 'BFRE', target: TAB_TARGET.bfre,
      statsOverride: [['BFRE', fmtChf(m.bfre)], ['DSO', m.dso != null ? `${Math.round(m.dso)} ${p('j')}` : '—'], ['DPO', m.dpo != null ? `${Math.round(m.dpo)} ${p('j')}` : '—']],
    },
  ];

  // ── Séries mensuelles pour les graphiques rate/level (réel + prévision) ────
  const rendementM = (revM && persM)
    ? revM.map((v, i) => (persM[i] ? num(v) / Math.abs(num(persM[i])) : null)) : null;
  const attach = {
    va: { series: vaM, caMonthly: revM, actualN: cc },
    mb: { series: mbM, caMonthly: revM, actualN: cc },
    eb: { series: ebM, caMonthly: revM, actualN: cc },
    caept: { series: fm.kpi?.revPerEptAnnMonths || null, actualN: cc },
    ratioept: { series: rendementM, actualN: cc },
  };
  kpis.forEach((k) => { if (attach[k.key]) Object.assign(k, attach[k.key]); });

  return kpis;
}

/* Cascade CA → EBITDA à ce jour (montants YTD réels). */
function buildCascadeSteps(ca, cogs, pers, indirect, va, mb, eb) {
  if (ca == null) return null;
  return [
    { label: tp('phrases:p_b7263d35bb'), val: ca, kind: 'start' },
    { label: tp('phrases:p_ac86ed0c8c'), val: num(cogs), kind: 'delta' },
    { label: tp('phrases:p_a372154f84'), val: num(va), kind: 'sub' },
    { label: tp('phrases:p_87eecd7729'), val: num(pers), kind: 'delta' },
    { label: tp('phrases:p_72223e53da'), val: num(mb), kind: 'sub' },
    { label: tp('phrases:p_5fca3ad3c2'), val: num(indirect), kind: 'delta' },
    { label: 'EBITDA', val: num(eb), kind: 'sub' },
  ];
}

/* Runway : coûts fixes mensuels (burn réel = salaires + charges indirectes/12)
   projetés à plat vs trésorerie projetée. Pas de ventilation personnel/autres
   fabriquée : le modèle ne l'expose pas séparément → une seule série. */
function buildRunway(m, cash, cc) {
  if (!Array.isArray(cash.runLiq) || m.burn == null) return null;
  const start = Math.max(0, cc - 1);
  const treso = cash.runLiq.slice(start, start + 6).map((v) => (Number.isFinite(v) ? v : null));
  if (treso.filter((v) => v != null).length < 2) return null;
  const burn = Math.abs(m.burn);
  const months = treso.map((_, i) => (i === 0 ? 'M' : `M+${i}`));
  return { months, fixe: treso.map(() => burn), treso };
}

/* Composition mensuelle du FRE : lignes BFRE réelles (forecastModel) + trésorerie. */
function buildFreStack(fm) {
  const comps = fm.wc?.components;
  if (!Array.isArray(comps) || !comps.length) return null;
  // BFRE = créances + stocks − dettes fournisseurs − acomptes (SANS trésorerie).
  const out = [];
  comps.forEach((c) => {
    const vals = (c.vals || []).map((v) => (v == null ? 0 : Math.abs(num(v))));
    // Sens porté par la section du bilan (actif + / passif −), pas par le libellé.
    const neg = c.sgn < 0;
    // Couleurs pleines (charte) par clé standard : créances = ardoise · stocks =
    // sage · fournisseurs = amber · acomptes = gold. Ligne propre au tenant (sans
    // clé) → sage par défaut. Plus de trames (hachuré/pointillé retirés).
    const COLOR_BY_STD = {
      ar_clients: 'var(--color-accent)',
      inventory: 'var(--color-sage)',
      ap_suppliers: 'var(--color-amber)',
      customer_advances: 'var(--color-gold)',
    };
    const color = COLOR_BY_STD[c.stdKey] || 'var(--color-sage)';
    out.push({ label: c.label, color, neg, m: vals });
  });
  return out.length ? out : null;
}

/* Sparkline : derniers points réels (mois clos) d'une série mensuelle. */
function tailSpark(arr, cc) {
  if (!Array.isArray(arr)) return null;
  const real = arr.slice(0, Math.max(0, cc)).filter((v) => Number.isFinite(v));
  return real.length >= 2 ? real.slice(-6) : null;
}

/* Valeur principale d'un KPI selon son type + l'unité active (kchf/chf). */
export function fmtKpiValue(kpi, unit = 'kchf') {
  if (kpi.valueOverride != null) return kpi.valueOverride;   // valeur pré-formatée (ex. « −25 pts »)
  const v = kpi.ytd;
  if (v == null || !Number.isFinite(Number(v))) return '–';
  if (kpi.type === 'ratio') return Number(v).toFixed(2);
  if (kpi.type === 'mois') return String(Math.round(Number(v)));
  const div = unit === 'chf' ? 1 : 1000;
  const n = Math.round(Number(v) / div);
  const neg = n < 0;
  const s = Math.abs(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return (neg ? '−' : '') + s;
}

/* Unité affichée à côté de la valeur (ratio → ×). */
export function unitLabel(kpi, unit = 'kchf') {
  if (kpi.type === 'ratio') return '×';
  if (kpi.type === 'mois') return tp('phrases:p_339528c893');
  return unit === 'chf' ? 'CHF' : 'kCHF';
}

/* Formatage d'une valeur chf/ratio/mois pour les 3 stats du drawer. */
export function fmtStat(v, kpi, unit = 'kchf') {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  if (kpi.type === 'ratio') return `${Number(v).toFixed(2)}×`;
  if (kpi.type === 'mois') return `${Math.round(Number(v))} ${tp('phrases:p_339528c893')}`;
  const div = unit === 'chf' ? 1 : 1000;
  const n = Math.round(Number(v) / div);
  const neg = n < 0;
  const s = Math.abs(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return `${neg ? '−' : ''}${s} ${unit === 'chf' ? 'CHF' : 'kCHF'}`;
}

/* Formatage kCHF compact (apostrophe suisse), pour les libellés de stats. */
function fmtChf(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Math.round(Number(v) / 1000);
  const neg = n < 0;
  const s = Math.abs(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return (neg ? '−' : '') + s + ' kCHF';
}
